import { makeAutoObservable, runInAction } from 'mobx'
import { type IInboxCombine } from 'entities/Inbox'
import { InboxesApi } from 'entities/Inbox/api/inboxes'
import { UsersApi } from 'entities/Users'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { type InboxesStore } from 'entities/Inbox/store/inboxes'

export class GroupInboxStore {
  constructor(private _inboxesStore: InboxesStore) {
    makeAutoObservable(this)
  }

  isGroupChild = (id: number | string): boolean => {
    return this._inboxesStore.personalInboxes.some(
      (inbox) => inbox.type === 'inbox_group' && inbox.teamsIds.some((teamId) => teamId === id)
    )
  }

  hasActiveChildInbox = (id?: number): boolean => {
    if (!id) return false

    const inbox = this._inboxesStore.getItem(id)
    if (!inbox || !inbox.isGroupInbox || !this._inboxesStore.currentInboxId) return false

    return inbox.teamsIds.some((teamId) => teamId === this._inboxesStore.currentInboxId)
  }

  isChildOfActiveGroupInbox = (id?: number | string): boolean => {
    if (!this._inboxesStore.currentInbox?.isGroupInbox || !id) return false

    return this._inboxesStore.currentInbox.teamsIds.some((teamId) => teamId === id)
  }

  isChildOfGroupInboxWithActiveChild = (id?: number): boolean => {
    if (!id) return false

    const inbox = this._inboxesStore.getItem(id)
    if (!inbox || inbox.isGroupInbox || !this._inboxesStore.currentInboxId) return false

    const inboxesGroups = this._inboxesStore.personalInboxes.filter((item) => item.isGroupInbox)
    const activeGroupInbox = inboxesGroups.find((item) =>
      item.teamsIds.some((teamId) => teamId === this._inboxesStore.currentInboxId)
    )

    if (!activeGroupInbox) return false
    return activeGroupInbox.teamsIds.some((teamId) => teamId === id)
  }

  addTeamToGroup = async (inbox: GroupInbox, groupId: number) => {
    const groupInbox = this._inboxesStore.getItem(groupId)

    if (groupInbox && groupInbox.type === 'inbox_group') {
      if (groupInbox.teamsIds.find((teamId) => teamId === inbox.id)) return

      await InboxesApi.createTeamsGroupsByIdAddTeams(
        groupId,
        inbox.bodyCreateTeamsGroupsByIdAddTeams
      )

      if (!groupInbox.teamsIds.find((teamId) => teamId === inbox.id)) {
        groupInbox.teamsIds.push(inbox.id)
      }
    }
  }

  removeTeamFromGroup = async (inbox: IInboxCombine, groupInbox?: IInboxCombine) => {
    if (!groupInbox) return
    if (groupInbox.type !== 'inbox_group') return

    await InboxesApi.deleteTeamsGroupsByIdRemoveTeamById(groupInbox.id, inbox.id)
    groupInbox.removeTeamId(inbox.id)
  }

  deleteGroup = async (inbox: GroupInbox) => {
    await UsersApi.updateUsersTeam({
      id: 0,
      type: 3,
    })
    InboxesApi.deleteTeamsGroupsById(inbox.id)
  }

  renameGroup = async (name: string, inbox: GroupInbox) => {
    if (inbox.name === name) {
      this.toggleRenameMode(inbox, false)

      return
    }

    InboxesApi.updateTeamsGroupsById(inbox.id, {
      ...inbox.bodyUpdateTeamsGroupsById,
      name: name,
    })

    inbox.name = name

    this.toggleRenameMode(inbox, false)
  }

  toggleRenameMode = (inbox: GroupInbox, status: boolean) => {
    runInAction(() => {
      inbox.is_rename = status
    })
  }

  removeChildFromGroup = (idGroup: number, idChild: number) => {
    const itemGroup = this._inboxesStore.getItem(idGroup)

    if (itemGroup instanceof GroupInbox) {
      itemGroup.removeTeamId(idChild)
    }
  }
}
